/**
 * Package protection: customer toggle adds/removes the protection variant.
 * Optional auto-add when cart drawer loads (theme setting; off by default).
 * Free e-book: auto-add when cart has items; mystery gift: when subtotal reaches rewards threshold.
 * Auto-gift lines use a per-variant lock so cart drawer + /cart page (or parallel section swaps) cannot double /cart/add.
 */
const PURE_CART_PP_DECLINED = 'pure_cart_pp_declined';
const PURE_CART_EBOOK_REMOVED = 'pure_cart_ebook_removed';
const PURE_CART_EBOOK_ADDED_BY_THEME = 'pure_cart_ebook_added_by_theme';
const PURE_CART_MYSTERY_REMOVED = 'pure_cart_mystery_removed';
const PURE_CART_MYSTERY_ADDED_BY_THEME = 'pure_cart_mystery_added_by_theme';
/** Prevents duplicate /cart/add when two drawer DOM updates run auto-add before the first add completes. */
const pureCartPackageProtectionAddInFlight = new Set();
/** Same race as package protection when cart drawer + cart page both mount auto-add elements, or cart-update + refetch. */
const pureCartAutoGiftLineInFlight = new Set();

function pureCartWithAutoGiftLock(variantId, fn) {
  const idKey = String(variantId);
  if (pureCartAutoGiftLineInFlight.has(idKey)) return;
  pureCartAutoGiftLineInFlight.add(idKey);
  Promise.resolve()
    .then(fn)
    .catch(() => {})
    .finally(() => {
      pureCartAutoGiftLineInFlight.delete(idKey);
    });
}

function pureCartCartHasVariant(cart, variantId) {
  const id = Number(variantId);
  return cart.items.some((item) => item.variant_id === id);
}

function pureCartSyncEbookFlags(cart, variantId) {
  const id = Number(variantId);
  const hasEbook = cart.items.some((item) => item.variant_id === id);
  if (cart.item_count === 0) {
    sessionStorage.removeItem(PURE_CART_EBOOK_REMOVED);
    sessionStorage.removeItem(PURE_CART_EBOOK_ADDED_BY_THEME);
    return;
  }
  if (sessionStorage.getItem(PURE_CART_EBOOK_ADDED_BY_THEME) === '1' && !hasEbook) {
    sessionStorage.setItem(PURE_CART_EBOOK_REMOVED, '1');
    sessionStorage.removeItem(PURE_CART_EBOOK_ADDED_BY_THEME);
  }
}

function pureCartSyncMysteryFlags(cart, variantId) {
  const id = Number(variantId);
  const hasLine = cart.items.some((item) => item.variant_id === id);
  if (cart.item_count === 0) {
    sessionStorage.removeItem(PURE_CART_MYSTERY_REMOVED);
    sessionStorage.removeItem(PURE_CART_MYSTERY_ADDED_BY_THEME);
    return;
  }
  if (sessionStorage.getItem(PURE_CART_MYSTERY_ADDED_BY_THEME) === '1' && !hasLine) {
    sessionStorage.setItem(PURE_CART_MYSTERY_REMOVED, '1');
    sessionStorage.removeItem(PURE_CART_MYSTERY_ADDED_BY_THEME);
  }
}

if (!customElements.get('cart-package-protection')) {
  customElements.define(
    'cart-package-protection',
    class CartPackageProtection extends HTMLElement {
      connectedCallback() {
        this._input = this.querySelector('input[type="checkbox"]');
        if (!this._input) return;
        this._input.addEventListener('change', this._onChange.bind(this));
        if (this.dataset.autoAdd === 'true') {
          queueMicrotask(() => this._tryAutoAdd());
        }
      }

      _tryAutoAdd() {
        if (sessionStorage.getItem(PURE_CART_PP_DECLINED) === '1') return;
        const variantId = this.dataset.variantId;
        if (!variantId) return;
        if (this.dataset.line) return;

        const idKey = String(variantId);
        if (pureCartPackageProtectionAddInFlight.has(idKey)) return;
        pureCartPackageProtectionAddInFlight.add(idKey);

        fetch(`${window.routes.cart_url}.js`)
          .then((r) => r.json())
          .then((cart) => {
            if (!cart || cart.item_count === 0) return;
            if (pureCartCartHasVariant(cart, variantId)) return;
            return fetch(`${window.routes.cart_add_url}`, {
              ...fetchConfig(),
              body: JSON.stringify({
                items: [{ id: Number(variantId), quantity: 1 }],
              }),
            })
              .then((r) => r.json())
              .then((state) => {
                if (state.status) return;
                sessionStorage.removeItem(PURE_CART_PP_DECLINED);
                publish(PUB_SUB_EVENTS.cartUpdate, { source: 'pure-package-protection', cartData: state });
              });
          })
          .catch(() => {})
          .finally(() => {
            pureCartPackageProtectionAddInFlight.delete(idKey);
          });
      }

      _onChange() {
        const variantId = this.dataset.variantId;
        const line = this.dataset.line;
        const checked = this._input.checked;

        if (!variantId) return;

        if (checked) {
          sessionStorage.removeItem(PURE_CART_PP_DECLINED);
        } else {
          sessionStorage.setItem(PURE_CART_PP_DECLINED, '1');
        }

        this._input.disabled = true;

        const done = () => {
          this._input.disabled = false;
        };

        if (checked) {
          const idKey = String(variantId);
          if (pureCartPackageProtectionAddInFlight.has(idKey)) {
            done();
            return;
          }
          pureCartPackageProtectionAddInFlight.add(idKey);

          fetch(`${window.routes.cart_add_url}`, {
            ...fetchConfig(),
            body: JSON.stringify({
              items: [{ id: Number(variantId), quantity: 1 }],
            }),
          })
            .then((r) => r.json())
            .then((state) => {
              if (state.status) {
                this._input.checked = false;
                return;
              }
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'pure-package-protection', cartData: state });
            })
            .catch(() => {
              this._input.checked = false;
            })
            .finally(() => {
              pureCartPackageProtectionAddInFlight.delete(idKey);
              done();
            });
        } else if (line) {
          fetch(`${window.routes.cart_change_url}`, {
            ...fetchConfig(),
            body: JSON.stringify({
              line: Number(line),
              quantity: 0,
            }),
          })
            .then((r) => r.json())
            .then((state) => {
              if (state.errors || state.status) {
                this._input.checked = true;
                sessionStorage.removeItem(PURE_CART_PP_DECLINED);
                return;
              }
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'pure-package-protection', cartData: state });
            })
            .catch(() => {
              this._input.checked = true;
              sessionStorage.removeItem(PURE_CART_PP_DECLINED);
            })
            .finally(done);
        } else {
          this._input.checked = false;
          done();
        }
      }
    }
  );
}

if (!customElements.get('cart-free-ebook')) {
  customElements.define(
    'cart-free-ebook',
    class CartFreeEbook extends HTMLElement {
      connectedCallback() {
        this._variantId = this.dataset.variantId;
        if (!this._variantId) return;
        this._onCartUpdateBound = this._onCartUpdate.bind(this);
        this._cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this._onCartUpdateBound);
        queueMicrotask(() => this._tryAutoAdd());
      }

      disconnectedCallback() {
        if (this._cartUpdateUnsubscriber) {
          this._cartUpdateUnsubscriber();
          this._cartUpdateUnsubscriber = undefined;
        }
      }

      _onCartUpdate(event) {
        if (event && event.source === 'pure-free-ebook') return;
        const cart = event && event.cartData;
        if (cart && cart.items) {
          pureCartSyncEbookFlags(cart, this._variantId);
        }
        queueMicrotask(() => this._tryAutoAdd());
      }

      _tryAutoAdd() {
        clearTimeout(this._autoAddTimer);
        this._autoAddTimer = setTimeout(() => this._runAutoAdd(), 1200);
      }

      _runAutoAdd() {
        if (sessionStorage.getItem(PURE_CART_EBOOK_REMOVED) === '1') return;
        const variantId = this._variantId;
        if (!variantId) return;

        pureCartWithAutoGiftLock(variantId, async () => {
          const r = await fetch(`${window.routes.cart_url}.js`);
          const cart = await r.json();
          if (!cart || cart.item_count === 0) return;
          pureCartSyncEbookFlags(cart, variantId);
          if (sessionStorage.getItem(PURE_CART_EBOOK_REMOVED) === '1') return;
          if (pureCartCartHasVariant(cart, variantId)) return;
          const addRes = await fetch(`${window.routes.cart_add_url}`, {
            ...fetchConfig(),
            body: JSON.stringify({
              items: [{ id: Number(variantId), quantity: 1 }],
            }),
          });
          const state = await addRes.json();
          if (state.status) return;
          sessionStorage.setItem(PURE_CART_EBOOK_ADDED_BY_THEME, '1');
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'pure-free-ebook', cartData: state });
        });
      }
    }
  );
}

if (!customElements.get('cart-mystery-gift')) {
  customElements.define(
    'cart-mystery-gift',
    class CartMysteryGift extends HTMLElement {
      connectedCallback() {
        this._variantId = this.dataset.variantId;
        this._thresholdCents = Number(this.dataset.thresholdCents || 0);
        if (!this._variantId || !this._thresholdCents) return;
        this._onCartUpdateBound = this._onCartUpdate.bind(this);
        this._cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this._onCartUpdateBound);
        queueMicrotask(() => this._tryAutoAdd());
      }

      disconnectedCallback() {
        if (this._cartUpdateUnsubscriber) {
          this._cartUpdateUnsubscriber();
          this._cartUpdateUnsubscriber = undefined;
        }
      }

      _onCartUpdate(event) {
        if (event && event.source === 'pure-mystery-gift') return;
        const cart = event && event.cartData;
        if (cart && cart.items) {
          pureCartSyncMysteryFlags(cart, this._variantId);
        }
        queueMicrotask(() => this._tryAutoAdd());
      }

      _tryAutoAdd() {
        clearTimeout(this._autoAddTimer);
        this._autoAddTimer = setTimeout(() => this._runAutoAdd(), 1200);
      }

      _runAutoAdd() {
        if (sessionStorage.getItem(PURE_CART_MYSTERY_REMOVED) === '1') return;
        const variantId = this._variantId;
        const threshold = this._thresholdCents;
        if (!variantId || !threshold) return;

        pureCartWithAutoGiftLock(variantId, async () => {
          const r = await fetch(`${window.routes.cart_url}.js`);
          const cart = await r.json();
          if (!cart || cart.item_count === 0) return;
          pureCartSyncMysteryFlags(cart, variantId);
          if (sessionStorage.getItem(PURE_CART_MYSTERY_REMOVED) === '1') return;
          const subtotal = cart.items_subtotal_price;
          if (!subtotal || subtotal < threshold) return;
          if (pureCartCartHasVariant(cart, variantId)) return;
          const addRes = await fetch(`${window.routes.cart_add_url}`, {
            ...fetchConfig(),
            body: JSON.stringify({
              items: [{ id: Number(variantId), quantity: 1 }],
            }),
          });
          const state = await addRes.json();
          if (state.status) return;
          sessionStorage.setItem(PURE_CART_MYSTERY_ADDED_BY_THEME, '1');
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'pure-mystery-gift', cartData: state });
        });
      }
    }
  );
}
